<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    :before-close="handleClose"
    width="90vw"
    custom-class="file-preview-dialog"
    top="5vh"
  >
    <div class="file-preview-body">
      <!-- 图片预览 -->
      <div v-if="type === 'image'" class="image-preview">
        <img :src="safeUrl" />
      </div>

      <!-- PDF 预览 -->
      <iframe
        v-else-if="type === 'pdf'"
        :src="pdfUrl"
        class="pdf-preview"
      ></iframe>

      <!-- Office 预览 -->
      <iframe
        v-else-if="type === 'office'"
        :src="officePreviewUrl"
        class="office-preview"
      ></iframe>

      <!-- 文本预览 -->
      <pre v-else-if="type === 'text'" class="text-preview">{{ textContent }}</pre>

      <!-- 不支持的文件类型 -->
      <div v-else class="unsupported-preview">
        <p>该文件类型不支持在线预览</p>
        <el-button type="primary" @click="handleDownload">下载文件</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'FilePreview',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    url: {
      type: String,
      default: ''
    },
    fileName: {
      type: String,
      default: ''
    },
    fileExt: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      type: '', // image, pdf, office, text, unsupported
      textContent: '',
      officePreviewUrl: ''
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    dialogTitle() {
      const typeMap = {
        image: '图片预览',
        pdf: 'PDF预览',
        office: 'Office预览',
        text: '文本预览',
        unsupported: '文件预览'
      }
      const typeName = typeMap[this.type] || '文件预览'
      return this.fileName ? `${typeName} - ${this.fileName}` : typeName
    },
    safeUrl() {
      // 将 URL 中的 + 符号编码为 %2B，避免被解析为空格
      return this.url ? this.url.replace(/\+/g, '%2B') : ''
    },
    pdfUrl() {
      return this.safeUrl ? `${this.safeUrl}#toolbar=0` : ''
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetState()
        if (this.url) {
          this.resolveType(this.url)
        }
      }
    },
    url: {
      immediate: true,
      handler(newUrl) {
        if (newUrl) {
          this.resolveType(newUrl)
        }
      }
    }
  },
  methods: {
    // 编程式调用 API
    open(url, fileName) {
      this.$emit('update:visible', true)
      if (url) {
        console.log('url',url)  
        this.resolveType(url)
      }
    },
    // 根据文件后缀判断类型（优先用传入的 fileExt）
    resolveType(url) {
      if (!url) {
        this.type = ''
        return
      }

      // 优先使用传入的 fileExt，否则从 URL 提取
      let ext = this.fileExt ? ('.' + this.fileExt.toLowerCase()) : ''
      if (!ext) {
        const cleanUrl = url.split('?')[0].split('#')[0]
        const lastDot = cleanUrl.lastIndexOf('.')
        ext = lastDot !== -1 ? cleanUrl.substring(lastDot).toLowerCase() : ''
      }

      const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']
      const pdfExts = ['.pdf']
      const officeExts = ['.xls', '.xlsx', '.doc', '.docx']
      const textExts = ['.txt', '.log', '.csv']

      if (imageExts.includes(ext)) {
        this.type = 'image'
      } else if (pdfExts.includes(ext)) {
        this.type = 'pdf'
      } else if (officeExts.includes(ext)) {
        this.type = 'office'
        this.officePreviewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(this.safeUrl)}`
      } else if (textExts.includes(ext)) {
        this.type = 'text'
        this.fetchTextContent(this.safeUrl)
      } else {
        this.type = 'unsupported'
      }
    },
    // 获取文本内容
    fetchTextContent(url) {
      fetch(url)
        .then(response => response.text())
        .then(content => {
          this.textContent = this.escapeHtml(content)
        })
        .catch(() => {
          this.textContent = '文件加载失败'
        })
    },
    // HTML 转义
    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }
      return text.replace(/[&<>"']/g, m => map[m])
    },
    // 关闭对话框
    handleClose() {
      this.$emit('update:visible', false)
      this.resetState()
    },
    // 触发下载事件
    handleDownload() {
      this.$emit('download', this.url)
    },
    // 重置状态
    resetState() {
      this.type = ''
      this.textContent = ''
      this.officePreviewUrl = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.file-preview-body {
  height: calc(90vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview {
  max-width: 100%;
  max-height: 100%;
  overflow: auto;

  img {
    max-width: 100%;
    max-height: calc(90vh - 140px);
    object-fit: contain;
  }
}

.pdf-preview,
.office-preview {
  width: 100%;
  height: 100%;
  border: none;
}

.text-preview {
  width: 100%;
  max-height: 100%;
  overflow: auto;
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.unsupported-preview {
  text-align: center;
  padding: 40px;

  p {
    margin-bottom: 20px;
    color: #909399;
    font-size: 16px;
  }
}
</style>

<style lang="scss">
.file-preview-dialog {
  .el-dialog__body {
    padding: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/FilePreview.vue